// ExecSupport.js — "งานสนับสนุน" (pos 90/99)
//
// คำถามของหน้านี้: ฝ่ายสนับสนุนมีงานค้างหรือทรัพยากรตึงตัวตรงไหน
//
// ต่างจากหน้าอื่นตรงที่รวมสี่ฝ่ายไว้หน้าเดียวในระดับ "มีอะไรค้างไหม" เพราะแต่ละฝ่าย
// มีเรื่องให้ผู้บริหารดูไม่มากพอจะเป็นหน้าของตัวเอง แต่รวมกันแล้วต้องดูทุกสัปดาห์
// ใช้แท็บแทนการวางต่อกันยาว เพราะสี่ฝ่ายนี้ไม่เกี่ยวกันเลย
import React, { useMemo, useState } from "react";
import { Badge, Col, Nav, ProgressBar, Row, Table } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import {
  ExecPage, Panel, StatCard, EmptyState, ScrollTable,
  YearPicker, useExecYear, useExecData,
  fmtNum, fmtPct, fmtBaht, toNum,
} from "./ExecShell";

const TABS = [
  { key: "issues", label: "เรื่องแจ้งซ่อม", icon: "ri-tools-line" },
  { key: "adminOffice", label: "ธุรการ", icon: "ri-booklet-line" },
  { key: "assets", label: "พัสดุและครุภัณฑ์", icon: "ri-archive-line" },
  { key: "library", label: "ห้องสมุด", icon: "ri-book-2-line" },
  { key: "clubs", label: "ชุมนุม", icon: "ri-team-line" },
];

export default function ExecSupport() {
  const { years, yearId, setYearId } = useExecYear();
  const [tab, setTab] = useState("issues");
  const query = useMemo(() => ({ year_id: yearId }), [yearId]);
  const { data, loading, error } = useExecData("/api/executive/stats/support", query);

  const issues = useMemo(() => (data?.issues || { available: false, byDepartment: [] }), [data]);
  const adminOffice = useMemo(() => (data?.adminOffice || { available: false, byMonth: [] }), [data]);
  const inventory = data?.inventory || { available: false };
  const supply = data?.supply || { available: false };
  const library = useMemo(() => (data?.library || { available: false, byMonth: [] }), [data]);
  const clubs = data?.clubs || { available: false };

  const monthlyOptions = (categories, color) => ({
    chart: { type: "bar", toolbar: { show: false } },
    colors: [color],
    plotOptions: { bar: { borderRadius: 3, columnWidth: "52%" } },
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: { categories, labels: { style: { fontSize: "10px" } } },
    yaxis: { labels: { style: { colors: "#6e7985", fontSize: "11px" } } },
    grid: { borderColor: "rgba(72,94,144,0.08)" },
  });

  const clubSummary = clubs.summary || {};
  const assetStats = inventory.assetStats || {};
  const itemStats = supply.itemStats || {};
  const reqStats = supply.requisitionStats || {};
  const borrowStats = supply.borrowStats || {};

  return (
    <ExecPage
      title="งานสนับสนุน"
      icon="ri-service-line"
      lead="ธุรการ พัสดุ ห้องสมุด ชุมนุม และเรื่องแจ้งซ่อม — ดูว่ามีงานค้างตรงไหน"
      activeYear={data?.year}
      loading={loading}
      error={error}
      toolbar={<YearPicker years={years} yearId={yearId} onChange={setYearId} activeYear={data?.year} />}
    >
      <Nav variant="pills" className="mb-4 gap-2 flex-wrap" activeKey={tab} onSelect={(key) => setTab(key || "issues")}>
        {TABS.map((t) => (
          <Nav.Item key={t.key}>
            <Nav.Link eventKey={t.key}>
              <i className={`${t.icon} me-1`} />{t.label}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {tab === "issues" && (
        !issues.available ? <EmptyState message="ยังไม่มีระบบแจ้งซ่อมในฐานข้อมูลนี้" /> : (
          <>
            <Row className="g-3 mb-4">
              <Col sm={6} xl={3}>
                <StatCard icon="ri-inbox-fill" color="primary" label="เรื่องแจ้งทั้งหมด" value={fmtNum(issues.total)} sub="ในช่วงที่เลือก" />
              </Col>
              <Col sm={6} xl={3}>
                <StatCard icon="ri-loader-4-fill" color="info" label="ยังไม่ปิด" value={fmtNum(toNum(issues.open) + toNum(issues.inProgress))} sub={`รับเรื่องแล้ว ${fmtNum(issues.inProgress)}`} />
              </Col>
              <Col sm={6} xl={3}>
                <StatCard
                  icon="ri-alarm-warning-fill"
                  color={toNum(issues.overdue) > 0 ? "danger" : "success"}
                  label="ค้างเกิน 7 วัน" value={fmtNum(issues.overdue)}
                  sub="เรื่องที่ยังไม่มีใครปิด"
                  tone={toNum(issues.overdue) > 0 ? "danger" : undefined}
                />
              </Col>
              <Col sm={6} xl={3}>
                <StatCard icon="ri-checkbox-circle-fill" color="success" label="ปิดแล้ว" value={fmtPct(issues.donePercent)} sub={`${fmtNum(issues.done)} เรื่อง`} />
              </Col>
            </Row>
            <Row className="g-3 mb-4">
              <Col xs={12}>
                <Panel
                  title="แยกตามฝ่าย"
                  note="เวลาเฉลี่ยที่ใช้ปิดเรื่องบอกคุณภาพการบริการได้ดีกว่าจำนวนค้าง ฝ่ายที่ค้างน้อยเพราะไม่มีใครแจ้ง ต่างจากฝ่ายที่ค้างน้อยเพราะปิดเร็ว"
                >
                  <ScrollTable>
                    <Table className="table-sm mb-0 align-middle">
                      <thead>
                        <tr>
                          <th>ฝ่าย</th>
                          <th className="text-end">ทั้งหมด</th>
                          <th className="text-end">รอรับ</th>
                          <th className="text-end">กำลังทำ</th>
                          <th className="text-end">ปิดแล้ว</th>
                          <th className="text-end">ค้างเกิน 7 วัน</th>
                          <th className="text-end">เวลาปิดเฉลี่ย</th>
                        </tr>
                      </thead>
                      <tbody>
                        {issues.byDepartment.map((row) => (
                          <tr key={row.dept_id}>
                            <td className="fw-semibold">{row.dept_name}</td>
                            <td className="text-end">{fmtNum(row.total)}</td>
                            <td className="text-end">{fmtNum(row.open)}</td>
                            <td className="text-end">{fmtNum(row.inProgress)}</td>
                            <td className="text-end text-success">{fmtNum(row.done)}</td>
                            <td className="text-end">
                              {toNum(row.overdue) > 0
                                ? <Badge bg="danger">{fmtNum(row.overdue)}</Badge>
                                : <span className="text-secondary">—</span>}
                            </td>
                            <td className="text-end">{row.avgDays === null ? <span className="text-secondary">—</span> : `${row.avgDays} วัน`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </ScrollTable>
                </Panel>
              </Col>
            </Row>
          </>
        )
      )}

      {tab === "adminOffice" && (
        !adminOffice.available ? <EmptyState message="ยังไม่มีโมดูลงานธุรการในฐานข้อมูลนี้" /> : (
          <>
            <Row className="g-3 mb-4">
              <Col sm={6} xl={3}>
                <StatCard
                  icon="ri-folder-shield-2-fill"
                  color={toNum(adminOffice.memo_awaiting_exec) > 0 ? "warning" : "secondary"}
                  label="แฟ้มรอผู้บริหารพิจารณา"
                  value={fmtNum(adminOffice.memo_awaiting_exec)}
                  sub={`รอธุรการเสนอ ${fmtNum(adminOffice.memo_to_propose)} เรื่อง`}
                  to="/exec/memos"
                />
              </Col>
              <Col sm={6} xl={3}>
                <StatCard icon="ri-mail-download-fill" color="info" label="หนังสือรับรอดำเนินการ" value={fmtNum(adminOffice.incoming_pending)} sub="ยังไม่ได้ส่งต่อ" />
              </Col>
              <Col sm={6} xl={3}>
                <StatCard icon="ri-calendar-check-fill" color="primary" label="จองห้อง/รถรออนุมัติ" value={fmtNum(adminOffice.booking_pending)} sub="คำขอที่ยังไม่ตอบ" />
              </Col>
              <Col sm={6} xl={3}>
                <StatCard
                  icon="ri-file-list-3-fill"
                  color={toNum(adminOffice.resolutions_open) > 0 ? "warning" : "success"}
                  label="มติที่ประชุมยังไม่ปิด"
                  value={fmtNum(adminOffice.resolutions_open)}
                  sub={`หนังสือรับรองรอออก ${fmtNum(adminOffice.certificates_requested)}`}
                />
              </Col>
            </Row>
            <Row className="g-3 mb-4">
              <Col xl={7}>
                <Panel title="หนังสือรับเข้ารายเดือน" note="ปริมาณงานสำหรับประเมินอัตรากำลัง">
                  {(adminOffice.byMonth || []).length === 0 ? <EmptyState compact /> : (
                    <ReactApexChart
                      options={monthlyOptions(adminOffice.byMonth.map((m) => m.month), "#0d6efd")}
                      series={[{ name: "หนังสือรับ", data: adminOffice.byMonth.map((m) => toNum(m.incoming)) }]}
                      type="bar" height={280}
                    />
                  )}
                </Panel>
              </Col>
              <Col xl={5}>
                <Panel title="เรื่องอื่นที่ต้องติดตาม">
                  <div className="d-flex justify-content-between border-bottom py-2 fs-sm">
                    <span>จดหมายเหตุรายวันรอลงนาม</span>
                    <span className="fw-semibold">{fmtNum(adminOffice.daily_logs_awaiting_sign)}</span>
                  </div>
                  <div className="d-flex justify-content-between py-2 fs-sm">
                    <span>เอกสารใกล้ครบอายุจัดเก็บ</span>
                    <span className="fw-semibold">{fmtNum(adminOffice.archive_expiring)}</span>
                  </div>
                  <div className="fs-xs text-secondary mt-3">เอกสารที่ครบอายุจัดเก็บต้องทำลายตามระเบียบ ภายใน 60 วันข้างหน้า</div>
                </Panel>
              </Col>
            </Row>
          </>
        )
      )}

      {tab === "assets" && (
        <>
          <Row className="g-3 mb-4">
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-archive-fill" color="primary" label="ครุภัณฑ์ทั้งหมด"
                value={inventory.available ? fmtNum(assetStats.total_assets) : "—"}
                sub={inventory.available ? `มูลค่า ${fmtBaht(assetStats.total_value)}` : "ยังไม่มีโมดูลครุภัณฑ์"}
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-tools-fill"
                color={toNum(assetStats.repair_assets) > 0 ? "warning" : "secondary"}
                label="อยู่ระหว่างซ่อม"
                value={inventory.available ? fmtNum(assetStats.repair_assets) : "—"}
                sub={inventory.available ? `สูญหาย ${fmtNum(assetStats.lost_assets)} รายการ` : ""}
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-alert-fill"
                color={toNum(itemStats.low_stock_count) > 0 ? "danger" : "success"}
                label="วัสดุต่ำกว่าจุดสั่งซื้อ"
                value={supply.available ? fmtNum(itemStats.low_stock_count) : "—"}
                sub={supply.available ? `หมดสต็อก ${fmtNum(itemStats.out_of_stock_count)} รายการ` : "ยังไม่มีโมดูลพัสดุ"}
                tone={toNum(itemStats.low_stock_count) > 0 ? "danger" : undefined}
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-hand-heart-fill"
                color={toNum(borrowStats.overdue_borrows) > 0 ? "warning" : "secondary"}
                label="ของยืมเกินกำหนดคืน"
                value={supply.available ? fmtNum(borrowStats.overdue_borrows) : "—"}
                sub={supply.available ? `กำลังยืมอยู่ ${fmtNum(borrowStats.active_borrows)} รายการ` : ""}
              />
            </Col>
          </Row>
          {supply.available && (
            <Row className="g-3 mb-4">
              <Col xl={6}>
                <Panel title="ใบเบิกวัสดุ">
                  <div className="d-flex justify-content-between border-bottom py-2 fs-sm">
                    <span>รอจ่าย</span>
                    <span className="fw-semibold">{fmtNum(reqStats.pending_count)}</span>
                  </div>
                  <div className="d-flex justify-content-between py-2 fs-sm">
                    <span>จ่ายแล้ว</span>
                    <span className="fw-semibold">{fmtNum(reqStats.issued_count)}</span>
                  </div>
                </Panel>
              </Col>
              <Col xl={6}>
                <Panel title="รายการวัสดุ">
                  <div className="d-flex justify-content-between border-bottom py-2 fs-sm">
                    <span>รายการที่ใช้งานอยู่</span>
                    <span className="fw-semibold">{fmtNum(itemStats.active_items)} / {fmtNum(itemStats.total_items)}</span>
                  </div>
                  <ProgressBar
                    className="mt-2" style={{ height: 8 }}
                    now={toNum(itemStats.total_items) > 0 ? (toNum(itemStats.low_stock_count) / toNum(itemStats.total_items)) * 100 : 0}
                    variant="danger"
                  />
                  <div className="fs-xs text-secondary mt-2">แถบแดงคือสัดส่วนรายการที่ต่ำกว่าจุดสั่งซื้อ</div>
                </Panel>
              </Col>
            </Row>
          )}
        </>
      )}

      {tab === "library" && (
        !library.available ? <EmptyState message="ยังไม่มีโมดูลห้องสมุดในฐานข้อมูลนี้" /> : (
          <>
            <Row className="g-3 mb-4">
              <Col sm={6} xl={3}>
                <StatCard icon="ri-book-open-fill" color="primary" label="การยืมในช่วงนี้" value={fmtNum(library.loans)} sub={`กำลังยืมอยู่ ${fmtNum(library.active)} เล่ม`} />
              </Col>
              <Col sm={6} xl={3}>
                <StatCard
                  icon="ri-time-fill"
                  color={toNum(library.overdue) > 0 ? "warning" : "success"}
                  label="ค้างคืน" value={fmtNum(library.overdue)}
                  sub={`สูญหาย ${fmtNum(library.lost)} เล่ม`}
                />
              </Col>
              <Col sm={6} xl={3}>
                <StatCard icon="ri-book-2-fill" color="info" label="รายการหนังสือ" value={fmtNum(library.titles)} sub={`${fmtNum(library.copies)} เล่ม`} />
              </Col>
              <Col sm={6} xl={3}>
                <StatCard icon="ri-add-box-fill" color="success" label="เพิ่มใหม่ปีนี้" value={fmtNum(library.newThisYear)} sub="เล่มที่ลงทะเบียนใหม่" />
              </Col>
            </Row>
            <Row className="g-3 mb-4">
              <Col xs={12}>
                <Panel title="การยืมรายเดือน">
                  {library.byMonth.length === 0 ? <EmptyState compact /> : (
                    <ReactApexChart
                      options={monthlyOptions(library.byMonth.map((m) => m.month), "#20c997")}
                      series={[{ name: "การยืม", data: library.byMonth.map((m) => toNum(m.loans)) }]}
                      type="bar" height={280}
                    />
                  )}
                </Panel>
              </Col>
            </Row>
          </>
        )
      )}

      {tab === "clubs" && (
        !clubs.available ? <EmptyState message="ยังไม่มีโมดูลชุมนุมในฐานข้อมูลนี้" /> : (
          <Row className="g-3 mb-4">
            <Col sm={6} xl={3}>
              <StatCard icon="ri-team-fill" color="primary" label="ชุมนุมที่เปิด" value={fmtNum(clubSummary.total_clubs)} sub={`ชาย ${fmtNum(clubSummary.male_clubs)} · หญิง ${fmtNum(clubSummary.female_clubs)}`} />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-user-add-fill" color="info" label="นักเรียนที่ลงชุมนุมแล้ว"
                value={fmtNum(clubSummary.enrolled)}
                sub={`จากที่นั่ง ${fmtNum(clubSummary.total_quota)} · ว่าง ${fmtNum(clubSummary.vacancies)}`}
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-user-unfollow-fill"
                color={toNum(clubSummary.clubs_without_advisor) > 0 ? "danger" : "success"}
                label="ชุมนุมที่ไม่มีครูที่ปรึกษา"
                value={fmtNum(clubSummary.clubs_without_advisor)}
                sub="ต้องหาครูรับผิดชอบ"
                tone={toNum(clubSummary.clubs_without_advisor) > 0 ? "danger" : undefined}
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                icon="ri-file-edit-fill"
                color={toNum(clubSummary.pending_assessments) > 0 ? "warning" : "success"}
                label="ประเมินชุมนุมค้าง"
                value={fmtNum(clubSummary.pending_assessments)}
                sub="นักเรียนที่ยังไม่ถูกประเมิน"
              />
            </Col>
          </Row>
        )
      )}
    </ExecPage>
  );
}
